import { back, type Screen } from '@/app'
import { AGE_GATE_FROM, evaluateAge, loadAgeIfNeeded, saveBirthDate, trailerAllowed } from '@/lib/ageGate'
import type { ContentCard } from '@/lib/catalog'
import { raf } from '@/lib/compat'
import { el, setText } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { IMAGE_WIDTH, mediaUrl, queueImage } from '@/lib/images'
import { toRemoteKey } from '@/lib/keys'
import { startMutedTrailer } from '@/lib/trailer'

/**
 * Brauzer variantının ORTAQ UI parçaları (kart · sıra · şəbəkə · düymə · modal).
 *
 * ⚠ CİHAZ APP-ININ `ui/card.ts`-i İLƏ QARIŞDIRMA. O, fokus şəbəkəsi üçün sabit
 *   px mövqeli kartlar qurur və hər fokus addımında `transform` ilə sürüşür.
 *   Buradakılar isə DOĞMA sənəd axınındadır: `<button>` semantikası, doğma
 *   `:focus`, doğma `overflow-x` sürüşməsi. Səbəb `lib/platform.ts`-dədir.
 *
 * ⚠ HƏR İNTERAKTİV ELEMENT `<button>`-dur, `<div>` DEYİL: brauzerdə Tab sırası,
 *   Enter/Space aktivləşdirməsi və ekran oxuyucusu pulsuz gəlir. TV brauzerində
 *   Tab pultun ox düymələri ilə idarə olunan yeganə etibarlı yoldur (ox düymələri
 *   brauzerin özünə gedir) — yəni bu, estetik deyil, İDARƏOLUNMA qərarıdır.
 */

/**
 * Brauzer ekranını `Screen` müqaviləsinə bükür.
 *
 * ⚠ `takeFocus`/`releaseFocus` BOŞDUR və bu, qəsdəndir: fokus halqasını burada
 *   BRAUZER idarə edir (`:focus`), cihazdakı `FocusGrid` deyil. Ekranın qurulan
 *   kimi özünə fokus alması səhifəni sürüşdürərdi (`element.focus()` scroll
 *   tetikləyir) — istifadəçi hər keçiddə tullanma görərdi.
 * ⚠ `onKey` YALNIZ «geri»ni tutur: qalan düymələr brauzerin öz naviqasiyasına
 *   (Tab · ox · Enter) aiddir. Bax `browser/shell.ts` — klaviatura körpüsü.
 */
/**
 * Ekranın arxaya düşəndə/geri qayıdanda öz işi (treyler dayandır/başlat) — `webScreen`-in
 * üçüncü arqumenti. Obyektin VERİLMƏSİ ekranın saxlanmağa razı olduğunu bildirir.
 */
export interface WebKeepAlive {
  park?: () => void
  unpark?: () => void
}

export function webScreen(node: HTMLElement, destroy?: () => void, keepAlive?: WebKeepAlive): Screen {
  const screen: Screen = {
    node,
    takeFocus() {},
    releaseFocus() {},
    onKey(key) {
      if (key === 'back') back()
    },
    destroy: destroy || (() => {}),
  }
  if (!keepAlive) return screen
  /*
    2026-10-23: «GERİ» OLDUĞUN YERƏ QAYTARIR — sürüşmə və fokus saxlanılır.
      NƏ TAPILDI (komanda bildirişi «Back must return to where I was»): Kataloq → janr →
      aşağı sürüşmə → film → İzlə → Geri. `app.ts` → `back()` ekranı SIFIRDAN qururdu,
      `shell.ts` → `applyRoute` səhifəni başa atırdı, `takeFocus` isə boşdur.
      ƏVVƏL: 1-ci səhifə, janr filtri sıfırlanmış, fokus `<body>`-də.
      NİYƏ BELƏ: ekran sökülmür (`app.ts` saxlayır), DOM yenidən qurulmur — PERFORMANCE.md-nin
      «DOM bir dəfə qurulur» qaydası; burada yalnız pəncərənin mövqeyi və fokus elementi
      yadda saxlanılır. Layout oxunmur: `pageYOffset` hazır sürüşmə dəyəridir.
      Fokus ƏVVƏL qoyulur, sürüşmə SONRA — `focus()` elementi görünən sahəyə çəkir,
      son söz saxlanmış mövqedə qalsın.
  */
  let savedY = 0
  let savedFocus: HTMLElement | null = null
  screen.park = () => {
    savedY = window.pageYOffset || document.documentElement.scrollTop || 0
    const active = document.activeElement as HTMLElement | null
    savedFocus = active && active !== document.body && active !== document.documentElement ? active : null
    if (keepAlive.park) keepAlive.park()
  }
  screen.unpark = () => {
    requeueImages(node)
    if (keepAlive.unpark) keepAlive.unpark()
    const target = savedFocus
    savedFocus = null
    if (target && document.documentElement.contains(target)) target.focus()
    window.scrollTo(0, savedY)
  }
  return screen
}

/**
 * Saxlanmış ekranın yarımçıq qalmış afişaları yenidən növbəyə düşür.
 * ⚠ Oynadıcıya keçid `bumpGeneration()` ilə növbəni ləğv edir — sifarişi verilib,
 *   amma `src`-i yazılmamış kart geri qayıdanda boş qalardı.
 */
function requeueImages(root: HTMLElement): void {
  const images = root.getElementsByTagName('img')
  for (let i = 0; i < images.length; i++) {
    const img = images[i] as HTMLImageElement
    const wanted = img.getAttribute('data-src')
    if (wanted && img.getAttribute('src') !== wanted) queueImage(img, wanted)
  }
}

/** Aksiya düyməsi. `ghost` = şəffaf + kənar xətt (Frontend/CLAUDE.md §3b). */
export function webButton(label: string, onClick: () => void, ghost?: boolean): HTMLButtonElement {
  const node = document.createElement('button')
  node.type = 'button'
  node.className = 'web-btn web-focusable' + (ghost ? ' web-btn--ghost' : '')
  node.appendChild(document.createTextNode(label))
  node.onclick = onClick
  return node
}

/**
 * Uzun təsvir — dörd sətirdən sonra kəsilir, «Daha çox» ilə açılır.
 *
 * Sahib tələbi (2026-08-12): «bəzi filmin descriptionu bir a4 qədər uzundursa o
 * hamısı görünməsin, üç dörd sətirdən sonra daha çox underline olsun və user
 * istəsə açıb oxusun».
 *
 * ⚠ DÜYMƏ YALNIZ MƏTN SIĞMAYANDA ƏLAVƏ OLUNUR — ölçü DOM-dan alınır
 *   (`scrollHeight > clientHeight`), simvol sayından təxmin edilmir: eyni mətn
 *   geniş pəncərədə üç, dar pəncərədə səkkiz sətir tutur.
 * ⚠ ÖLÇMƏ `raf` İLƏ TƏXİRƏ SALINIR: element hələ sənədə qoşulmayıb, qoşulmamış
 *   elementin `clientHeight`-i 0-dır və şərt HƏMİŞƏ doğru çıxardı.
 * ⚠ `<button>`-dur, `<a>` DEYİL: heç yerə aparmır (yuxarıdakı ümumi qeyd).
 */
export function webDescription(text: string, className?: string): HTMLElement {
  const wrap = el('div', 'web-desc')
  const body = el('p', (className || 'web-detail__desc') + ' web-desc__body', text)
  wrap.appendChild(body)

  raf(function () {
    if (body.scrollHeight - body.clientHeight <= 1) return
    const more = document.createElement('button')
    more.type = 'button'
    more.className = 'web-desc__more web-focusable'
    more.appendChild(document.createTextNode(T('Daha çox')))
    more.onclick = function () {
      const open = body.className.indexOf('is-open') >= 0
      body.className = open
        ? body.className.replace(' is-open', '')
        : body.className + ' is-open'
      setText(more, open ? T('Daha çox') : T('Gizlət'))
    }
    wrap.appendChild(more)
  })

  return wrap
}

/** Filtr çipi (kataloq janrları · növ). */
export function webChip(label: string, active: boolean, onClick: () => void): HTMLButtonElement {
  const node = document.createElement('button')
  node.type = 'button'
  node.className = 'web-chip web-focusable' + (active ? ' is-active' : '')
  node.appendChild(document.createTextNode(label))
  node.onclick = onClick
  return node
}

/** Boş/xəta vəziyyəti — «qara ekran yoxdur» qaydası (Frontend/CLAUDE.md §6). */
export function webEmpty(message: string): HTMLElement {
  return el('div', 'web-empty', message)
}

/** Səhifə qabı. */
export function webPage(narrow?: boolean): HTMLElement {
  return el('div', 'web-page' + (narrow ? ' web-page--narrow' : ''))
}

/** Başlıq. */
export function webTitle(text: string): HTMLElement {
  return el('h1', 'web-h1', text)
}

/** Kart üstü meta sətri — il · müddət · janr. */
/**
 * Müddətin QISA forması — «1s 42 dəq».
 * ⚠ Kart dardır və hover sətri təkdir; tam söz («1 saat 42 dəq») il ilə birlikdə
 *   kəsilirdi. Saytda da eyni qısa forma işlədilir (`lib/format.ts`).
 */
function shortRuntime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h <= 0) return m + T(' dəq')
  return m > 0 ? h + T('s') + ' ' + m + T(' dəq') : h + T(' saat')
}

function cardMeta(card: ContentCard): string {
  const parts: string[] = []
  if (card.year) parts.push(String(card.year))
  if (card.durationMinutes) parts.push(card.durationMinutes + T(' dəq'))
  const genres = card.genreNames
  if (genres && genres.length) parts.push(genres[0] as string)
  return parts.join(' · ')
}

/** Kart seçimləri — davam et zolağı və 16:9 forma yalnız bəzi sıralarda lazımdır. */
export interface CardOptions {
  /** 0…1 — «Davam et» sırasındakı izlənmə nisbəti. */
  progress?: number
  rank?: number
  /** 16:9 kadr (bölüm/canlı) — defolt 2:3 afişadır. */
  wide?: boolean
  /** Posterin əvəzinə göstəriləcək ünvan (bölüm kadrı). */
  imageUrl?: string | null
  /** Başlıq altındakı sətri əvəz edir (məs. premyera tarixi). */
  meta?: string
}

/**
 * Kataloq kartı.
 *
 * ⚠ ŞƏKİL `queueImage` İLƏ GƏLİR, birbaşa `img.src` ilə YOX: növbə paralel
 *   yükləməni məhdudlaşdırır və naviqasiyada nəsli sıfırlayır (`bumpGeneration`).
 *   TV brauzerinin şəbəkə yığını 40 eyni anda gedən şəkildə ilişir — bu, cihaz
 *   app-ında ölçülmüş donma səbəblərindən biridir və brauzerdə də eynidir.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KART HOVER ÖNGÖRÜNÜMÜ — SAYTDAKI DAVRANIŞ (sahib tələbi 2026-08-22:
 * «veb TV kinobox az projedə saytdakı kimi hover item yoxdur onu düzəlt»)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Saytdakı qayda (`apps/web/src/components/CardPreview.tsx`) burada eyni ilə
 * təkrarlanır: siçan kartın üstündə QƏSDLƏ dayananda treylerin 5-ci saniyəsindən
 * 10-cu saniyəsinə qədər SƏSSİZ parça kartın öz çərçivəsində oynayır.
 *
 * ⚠ NİYƏ 60 ms NİYYƏT HƏDDİ: kursor sıra boyu sürüşəndə onlarla kart altından
 *   keçir. Həddsiz hər keçid şəbəkəyə sorğu atardı — saytda ölçülmüş eyni səbəb.
 * ⚠ EYNİ ANDA YALNIZ BİR ÖNİZLƏMƏ: `stopActivePreview` əvvəlkini söndürür.
 *   «Canlı dekoder ≤ 1» qaydası (PERFORMANCE.md) TV brauzerində də keçərlidir —
 *   ucuz TV qutusunda ikinci dekoder açmaq ölçülmüş donma səbəbidir.
 * ⚠ TREYLER MƏNBƏYİ KART CAVABINDA YOXDUR: `startMutedTrailer` boş mənbə ilə
 *   çağırılanda serverin yüngül ucuna (`/api/catalog/content/{slug}/trailer`)
 *   özü gedir — yəni burada ƏLAVƏ sorğu məntiqi yazılmır.
 * ⚠ YAŞ QAPISI: qapı bağlıdırsa önizləmə OYNAMIR (MYTasks 712 — «18+ filmlərdə
 *   login edilməyibsə hover trailer play olmamalı»).
 * ⚠ `pointer-events: none` (CSS): video kartın öz klikini udmamalıdır.
 */
const HOVER_INTENT_MS = 60
const PREVIEW_FROM_SECONDS = 5
const PREVIEW_TO_SECONDS = 10

/** Hazırda oynayan önizləməni söndürən funksiya — eyni anda yalnız bir dənə. */
let stopActivePreview: (() => void) | null = null

function attachCardPreview(node: HTMLElement, frame: HTMLElement, card: ContentCard): void {
  let intent = 0
  let stopTrailer: (() => void) | null = null
  let video: HTMLVideoElement | null = null

  const teardown = (): void => {
    if (intent) {
      window.clearTimeout(intent)
      intent = 0
    }
    if (stopTrailer) {
      stopTrailer()
      stopTrailer = null
    }
    if (video && video.parentNode) video.parentNode.removeChild(video)
    video = null
    if (stopActivePreview === teardown) stopActivePreview = null
  }

  const begin = (): void => {
    intent = 0
    // Uşaq profilində önizləmə yoxdur (2026-09-25, BusinessRequirement 9.3) — `ui/cardPreview.ts` ilə eyni.
    if (document.documentElement.hasAttribute('data-kids')) return
    // Yaş qapısı bağlıdırsa treyler ümumiyyətlə açılmır.
    // 2026-10-25: `evaluateAge` girişsiz baxana «allowed» deyir və profil həddinə baxmır — 18+
    //   önizləmə girişsiz oynayırdı. Treyler qaydası saytla eynidir (`trailerAllowed`).
    if (!trailerAllowed(card.ageLimit)) return
    if (stopActivePreview) stopActivePreview()
    stopActivePreview = teardown

    const media = document.createElement('video')
    media.className = 'web-card__video'
    media.muted = true
    media.setAttribute('playsinline', '')
    /*
      ⚠ ŞƏKİLDƏN DƏRHAL SONRA QOYULUR, SONA ƏLAVƏ EDİLMİR (2026-08-23): CSS-də
        «treyler oynayanda ▶ gizlənsin» qaydası qardaş seçicisi ilə yazılıb
        (`.web-card__video.is-ready ~ .web-card__play`) — o, YALNIZ video düymədən
        ƏVVƏL gələndə işləyir. Sona əlavə etsəydik ▶ kadrın ortasında asılı qalardı.
      ⚠ Yığın sırası dəyişmir: panel (z2), ▶ (z3) və metal kənar (z4) açıq
        `z-index` aldığına görə videonun ÜSTÜNDƏ qalır.
    */
    frame.insertBefore(media, frame.children[1] || null)
    video = media

    /*
      ⚠ 5→10 SANİYƏ DÖVRƏSİ: `timeupdate` ilə saxlanılır, `loop` atributu ilə yox —
        `loop` bütöv treyleri təkrarlayardı, sahibin istədiyi isə qısa parçadır.
    */
    const clamp = (): void => {
      if (!video) return
      if (video.currentTime >= PREVIEW_TO_SECONDS) video.currentTime = PREVIEW_FROM_SECONDS
    }
    media.addEventListener('timeupdate', clamp, false)

    /*
      ⚠⚠ BAŞA ATLAMA YALNIZ BİR DƏFƏ (2026-10-01, TestingAI2 veb TV 23).
         NƏ ÖLÇÜLDÜ (tv.kinobox.az, Playwright, «Psixiatr» kartı, video hadisələri
         yazıldı): önizləmə 431 ms-də başladı, amma `currentTime` 4 saniyə boyu
         5.00-da QALDI — hər ~60 ms-də `playing → seeking → waiting → seeked →
         playing` dövrü. Səbəb: `startMutedTrailer` bu callback-i HƏM `loadeddata`,
         HƏM də HƏR `playing` hadisəsində çağırır (köhnə mühərrik ehtiyatı), callback
         isə hər dəfə şərtsiz 5-ə atlayırdı; atlama özü yeni `playing` doğurur.
         ƏVVƏL: kartın üstündə donmuş kadr, treyler heç vaxt irəliləmirdi.
         NİYƏ BU HƏLL: cihaz variantı (`ui/cardPreview.ts` → `ready`) eyni yolu
         `currentTime < başlanğıc` şərti ilə keçir və dövrə düşmür; burada əlavə
         olaraq bayraq var ki, 10 → 5 dövrəsindən (`clamp`) sonrakı `playing`
         də atlamanı təkrarlamasın.
    */
    let jumped = false
    stopTrailer = startMutedTrailer(
      media,
      null,
      card.slug,
      () => {
        if (!video) return
        // İlk kadr gəldi: parçanın başına atlayıb görünür edirik.
        if (!jumped && video.duration > PREVIEW_FROM_SECONDS + 0.5) {
          jumped = true
          if (video.currentTime < PREVIEW_FROM_SECONDS) video.currentTime = PREVIEW_FROM_SECONDS
        }
        video.className = 'web-card__video is-ready'
      },
      () => {
        // Treyler yoxdur/alınmadı → kart olduğu kimi (afişa) qalır.
        teardown()
      },
    )
  }

  node.addEventListener('mouseenter', () => {
    if (intent) window.clearTimeout(intent)
    intent = window.setTimeout(begin, HOVER_INTENT_MS)
  }, false)
  node.addEventListener('mouseleave', teardown, false)
  // Sıra sürüşəndə və ya ekran dəyişəndə kart DOM-dan çıxır — dekoder qalmasın.
  node.addEventListener('blur', teardown, false)
}

export function webCard(card: ContentCard, onOpen: (card: ContentCard) => void, options?: CardOptions): HTMLElement {
  const opts = options || {}
  const node = document.createElement('button')
  node.type = 'button'
  node.className = 'web-card web-focusable' + (opts.wide ? ' web-card--wide' : '')

  const frame = el('div', 'web-card__frame')
  const img = document.createElement('img')
  img.className = 'web-card__img'
  img.alt = ''
  const source = opts.imageUrl !== undefined ? opts.imageUrl : card.posterUrl
  if (source) queueImage(img, mediaUrl(source, IMAGE_WIDTH.card))
  frame.appendChild(img)
  if (opts.rank) frame.appendChild(el('span', 'web-card__rank', String(opts.rank)))

  if (card.ageLimit) frame.appendChild(el('span', 'web-card__badge', card.ageLimit + '+'))
  if (card.rating) frame.appendChild(el('span', 'web-card__rate', '★ ' + card.rating.toFixed(1)))

  if (opts.progress && opts.progress > 0) {
    const bar = el('div', 'web-card__progress')
    const fill = el('i')
    fill.style.width = Math.max(2, Math.min(100, Math.round(opts.progress * 100))) + '%'
    bar.appendChild(fill)
    frame.appendChild(bar)
  }

  /*
    ── HOVER QATI — SAYTDAKI KARTIN EYNİSİ (sahib düzəlişi 2026-08-23) ─────────

    ⚠ ƏVVƏL YALNIZ TREYLER OYNAYIRDI: kartın öz görünüşü (sakit və hover halı)
      saytdan fərqli idi — nişanlar həmişə görünürdü, ad posterin ALTINDA sabit
      qalırdı, ▶ düyməsi, alt məlumat zolağı və metal kənar isə ÜMUMİYYƏTLƏ yox idi.
      Sahibin şikayəti məhz bu idi: «hover play olur, amma dizaynı saytdakı kimi deyil».

    ⚠ SAYTDAKI QURULUŞ (`apps/web/src/components/ContentCard.tsx`):
        · sakit halda kart YALNIZ afişadır — nişanlar da, ad da gizlidir;
        · hover-də: kart bir az böyüyür, metal kənar boyunca işıq gəzir,
          yaş SOL-ÜST, reytinq SAĞ-ÜST nişanı açılır, posterin üst hissəsində ▶,
          altda isə ad + «il · müddət» + «janr · əhval» zolağı çıxır.

    ⚠ MƏTN SERVERDƏN GƏLİR, HOVER ANINDA SORĞU ATILMIR — `durationMinutes`,
      `genreNames`, `moodNames` kart cavabındadır (bax `lib/catalog.ts`). Saytda
      da eyni qərar verilib: əvvəl hər hover ayrıca detal sorğusu atırdı.
  */
  frame.appendChild(el('span', 'web-card__play', '▶'))

  const panel = el('span', 'web-card__hover')
  panel.appendChild(el('span', 'web-card__hover-title', card.title))

  // 1-ci sətir: il · müddət (yaş BURADA YAZILMIR — o, sol-üst nişandadır).
  const line1 = [
    card.year ? String(card.year) : '',
    card.durationMinutes ? shortRuntime(card.durationMinutes) : '',
  ].filter(Boolean).join(' · ')
  if (line1) panel.appendChild(el('span', 'web-card__hover-line', line1))

  // 2-ci sətir: janr (2) · əhval (1) — saytdakı eyni hədd; üç janr sətri qırır.
  const genres = card.genreNames || []
  const moods = card.moodNames || []
  const line2 = genres.slice(0, 2).concat(moods.slice(0, 1)).join(' · ')
  if (line2) panel.appendChild(el('span', 'web-card__hover-line', line2))

  frame.appendChild(panel)

  node.appendChild(frame)
  node.appendChild(el('div', 'web-card__title', card.title))
  const meta = opts.meta !== undefined ? opts.meta : cardMeta(card)
  if (meta) node.appendChild(el('div', 'web-card__meta', meta))

  node.onclick = () => onOpen(card)
  // Hover öngörünümü — izah `attachCardPreview`-dədir.
  attachCardPreview(node, frame, card)
  return node
}

/**
 * ŞƏXS KARTI — axtarış nəticəsindəki aktyor/rejissor sırası (2026-08-16).
 *
 * ⚠ NİYƏ AYRI KART, `webCard` DEYİL: film kartı 2:3 afişadır və şəxs fotosu orada
 *   kəsilir; həm də şəxs sətrində reytinq/yaş nişanı mənasızdır. Dairəvi kadr üç
 *   TV səthində eynidir (Android `PersonCard`, cihaz app-ının `buildPersonCard`-ı).
 * ⚠ Şəkil `queueImage` növbəsindən gəlir — film kartı ilə eyni səbəb (yuxarıda).
 */
export function webPersonCard(
  person: { name: string | null; photoUrl: string | null; roleName: string | null },
  onOpen: () => void,
): HTMLElement {
  const node = document.createElement('button')
  node.type = 'button'
  node.className = 'web-person web-focusable'

  const frame = el('div', 'web-person__frame')
  const img = document.createElement('img')
  img.className = 'web-person__img'
  img.alt = ''
  const url = mediaUrl(person.photoUrl, IMAGE_WIDTH.avatar)
  if (url) queueImage(img, url)
  frame.appendChild(img)
  node.appendChild(frame)

  node.appendChild(el('div', 'web-person__name', person.name || '—'))
  // Rol boş ola bilir (kataloqda yazılmayıb) — sətir boş qalmasın deyə «Şəxs».
  node.appendChild(el('div', 'web-person__role', person.roleName || T('Şəxs')))

  node.onclick = onOpen
  return node
}

/** Kartlar şəbəkəsi (kataloq · axtarış · siyahı). */
export function webGrid(cards: HTMLElement[]): HTMLElement {
  const grid = el('div', 'web-grid')
  for (let i = 0; i < cards.length; i++) grid.appendChild(cards[i] as HTMLElement)
  return grid
}

/**
 * Sürüşən sıra — başlıq + doğma `overflow-x` konteyner + iki ox.
 *
 * ⚠ OXLAR NİYƏ VAR: TV brauzerində siçan təkəri ÜFÜQİ sürüşdürmür və barmaq da
 *   yoxdur. Oxsuz sırada ilk 5-6 kartdan sonrasına ÇATMAQ MÜMKÜN OLMAZDI —
 *   klaviatura ilə Tab bassan brauzer sürüşdürür, amma bu, gözlə tapılan yol
 *   deyil. Oxlar həm işarəçi, həm Tab yolunda işləyir.
 */
export function webRow(
  title: string,
  cards: HTMLElement[],
  more?: { label: string; onClick: () => void },
  featured = false,
): HTMLElement {
  const row = el('div', 'web-row' + (featured ? ' web-row--recommended' : ''))

  const head = el('div', 'web-row__head')
  head.appendChild(el('h2', 'web-row__title', title))
  if (more) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'web-row__more web-focusable'
    button.appendChild(document.createTextNode(more.label))
    button.onclick = more.onClick
    head.appendChild(button)
  }
  row.appendChild(head)

  const wrap = el('div', 'web-row__wrap')
  const scroller = el('div', 'web-scroller')
  for (let i = 0; i < cards.length; i++) scroller.appendChild(cards[i] as HTMLElement)
  wrap.appendChild(scroller)
  wrap.appendChild(arrow(scroller, -1))
  wrap.appendChild(arrow(scroller, 1))
  row.appendChild(wrap)

  return row
}

/**
 * OX DÜYMƏSİ — sıralar VƏ hero üçün ORTAQ (2026-08-30).
 *
 * ⚠ NİYƏ İXRAC OLUNDU: hero-ya da ox lazım oldu (sahib tələbi: «vebtvdə coverde
 *   hero filmlərin sağ sol oxların qoy ki, user çevirə bilsin, aşağıdakı
 *   sectionlar kimi»). «Aşağıdakı sectionlar kimi» = MƏHZ BU düymə: eyni sinif,
 *   eyni ölçü, eyni qiyyuməcik simvolu, eyni `aria-label`. İkinci nüsxə yazsaydıq
 *   iki ox bir-birindən yavaş-yavaş ayrılardı.
 * ⚠ İŞ FUNKSİYASI KƏNARDAN VERİLİR (`run`): sıra üçün bu, `tweenScroll`
 *   bağlamasıdır, hero üçün isə növbəti hero-ya keçiddir — düymənin özü
 *   nə sürüşmə, nə hero haqqında heç nə bilmir.
 * ⚠ SİMVOL MƏTN DÜYÜNÜDÜR, `browser/icons.ts` İKONU DEYİL: dəyişiklik yoxdur,
 *   sıra oxu ilk gündən belədir (bir simvol + `font-size` — SVG parse etmir).
 */
export function webArrow(direction: number, run: () => void): HTMLButtonElement {
  const node = document.createElement('button')
  node.type = 'button'
  node.className = 'web-arrow web-focusable ' + (direction < 0 ? 'web-arrow--left' : 'web-arrow--right')
  node.setAttribute('aria-label', direction < 0 ? T('Geri sürüşdür') : T('İrəli sürüşdür'))
  node.appendChild(document.createTextNode(direction < 0 ? '‹' : '›'))
  node.onclick = run
  return node
}

/** Sıra oxu — bir «səhifə» sürüşdürür. */
function arrow(scroller: HTMLElement, direction: number): HTMLElement {
  return webArrow(direction, () => {
    // Görünən enin 85%-i — bir neçə kart ekranda qalsın ki, kontekst itməsin.
    tweenScroll(scroller, scroller.scrollLeft + direction * Math.round(scroller.clientWidth * 0.85))
  })
}

/** Sürüşməni yumşaldan taymer. */
const SCROLL_MS = 260

/**
 * ⚠ `scrollTo({behavior:'smooth'})` İŞLƏDİLMİR — Chrome 61+-dır, bu variant isə
 *   chrome53-ə yığılır (TV brauzeri). Dəstəklənməyən halda sürüşmə ANİ olardı
 *   (funksiya obyekt arqumenti tanımır və atır) — yəni bəzi TV-lərdə tullanma,
 *   bəzilərində yumşaq keçid. Öz tween-imiz hər yerdə eynidir.
 */
function tweenScroll(scroller: HTMLElement, to: number): void {
  const from = scroller.scrollLeft
  const max = scroller.scrollWidth - scroller.clientWidth
  const target = Math.max(0, Math.min(max, to))
  const delta = target - from
  if (!delta) return

  const started = Date.now()
  const step = (): void => {
    const passed = Date.now() - started
    if (passed >= SCROLL_MS) {
      scroller.scrollLeft = target
      return
    }
    // easeOutQuad — sonda yavaşıyır, başlanğıcda dərhal cavab verir.
    const t = passed / SCROLL_MS
    scroller.scrollLeft = from + delta * (1 - (1 - t) * (1 - t))
    raf(step)
  }
  raf(step)
}

// ── Pəncərələr (`.web-modal`) ──────────────────────────────────────────────

/*
  2026-10-23: `.web-modal` PƏNCƏRƏLƏRİ BİR REYESTRDƏDİR — «Geri» və naviqasiya onları bağlayır.
    NƏ TAPILDI: yaş bildirişi və doğum tarixi seçicisi `document.body`-yə qoşulurdu və YALNIZ
    öz «Bağla» düyməsi ilə silinirdi. Pultun «Geri»si (461 · 10009 · 166 · Esc) və brauzerin
    «Geri»si (`popstate`) onlara toxunmurdu, qabıq isə arxadakı ekranı dəyişirdi.
    ƏVVƏL: pəncərə NÖVBƏTİ ekranın üstündə qalırdı və `shell.ts` → `focusScope` oxları onun
    içinə bağladığı üçün fokus tələyə düşürdü.
    NİYƏ BELƏ: dinləyici `window`-un TUTMA fazasındadır — film modalının (`document`, tutma)
    və qabığın (`document`, qabarma) dinləyicilərindən ƏVVƏL işləyir, yəni bir basış yalnız
    ən üstdəki qatı bağlayır. `<select>` də istisna deyil: seçicidə «Geri» yazı düyməsi deyil.
    Bağlananda fokus pəncərəni açan elementə qayıdır (10-foot naviqasiyada ölü hal olmasın).
*/
interface WebDialog {
  node: HTMLElement
  restore: HTMLElement | null
  onCancel: (() => void) | null
}

const dialogs: WebDialog[] = []
let dialogKeysBound = false

interface DialogHandle {
  /** Proqram bağlayır (uğurlu axın) — `onCancel` çağırılmır. */
  close: () => void
  /** İstifadəçi imtina etdi («Bağla» · «Geri» · naviqasiya). */
  cancel: () => void
}

function openDialog(node: HTMLElement, onCancel?: () => void): DialogHandle {
  bindDialogKeys()
  const active = document.activeElement as HTMLElement | null
  const entry: WebDialog = {
    node,
    restore: active && active !== document.body ? active : null,
    onCancel: onCancel || null,
  }
  dialogs.push(entry)
  document.body.appendChild(node)
  return {
    close: () => removeDialog(entry, true, false),
    cancel: () => removeDialog(entry, true, true),
  }
}

function removeDialog(entry: WebDialog, restoreFocus: boolean, cancelled: boolean): void {
  const index = dialogs.indexOf(entry)
  if (index < 0) return
  dialogs.splice(index, 1)
  if (entry.node.parentNode) entry.node.parentNode.removeChild(entry.node)
  if (cancelled && entry.onCancel) entry.onCancel()
  if (restoreFocus && !dialogs.length && entry.restore && document.documentElement.contains(entry.restore)) {
    entry.restore.focus()
  }
}

/** Sənəddən kənarda silinmiş pəncərə (məs. `body` təmizləndi) reyestrdə qalmasın. */
function pruneDialogs(): void {
  for (let i = dialogs.length - 1; i >= 0; i--) {
    const entry = dialogs[i] as WebDialog
    if (!document.documentElement.contains(entry.node)) dialogs.splice(i, 1)
  }
}

/** Ən üstdəki pəncərəni bağlayır. Bağlayıbsa `true` — «Geri» başqa işə düşmür. */
export function closeTopWebModal(): boolean {
  pruneDialogs()
  const top = dialogs[dialogs.length - 1]
  if (!top) return false
  removeDialog(top, true, true)
  return true
}

/** Bütün pəncərələri bağlayır — ekran dəyişəndə (`shell.ts` → `applyRoute`). */
export function closeWebModals(): void {
  while (dialogs.length) removeDialog(dialogs[dialogs.length - 1] as WebDialog, false, true)
}

function bindDialogKeys(): void {
  if (dialogKeysBound) return
  dialogKeysBound = true
  window.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      if (!dialogs.length || toRemoteKey(event) !== 'back') return
      pruneDialogs()
      if (!dialogs.length) return
      const target = event.target as HTMLElement | null
      const tag = target && target.tagName ? target.tagName.toUpperCase() : ''
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      event.preventDefault()
      event.stopPropagation()
      closeTopWebModal()
    },
    true,
  )
}

// ── Yaş qapısı ─────────────────────────────────────────────────────────────

/**
 * Hökm gözlənilərkən «Yoxlanılır…» pəncərəsi bu qədər gecikmə ilə çıxır — sürətli
 * cavabda pəncərə bir anlıq yanıb-sönməsin.
 */
const AGE_WAIT_HINT_MS = 250

/** Son qapı çağırışının nömrəsi — yeni klik və ya imtina köhnə gözləməni ləğv edir. */
let gateTicket = 0

/**
 * Kartı açmazdan ƏVVƏL yaş qapısından keçirir.
 *
 * ⚠ SAHİB QƏRARI (MYTasks 3a · iOS 18+ şikayəti 2026-08-10): 18+ kart siyahıda
 *   QALIR, bağlanan yalnız AÇILIŞDIR — və qapı bağlayanda FİLM SƏHİFƏSİ
 *   AÇILMAMALIDIR, birbaşa modal çıxmalıdır. Ona görə yoxlama naviqasiyadan
 *   ƏVVƏLDİR, detal ekranının içində deyil.
 * ⚠ TƏHLÜKƏSİZLİK SƏRHƏDİ DEYİL — həqiqi qapı oynatma biletindədir.
 */
export function openWithAgeGate(card: ContentCard, open: () => void): void {
  const ticket = ++gateTicket
  if (evaluateAge(card.ageLimit) === 'allowed') {
    open()
    return
  }
  /*
    2026-10-23: «İCAZƏ YOXDUR» HÖKMÜ YAŞ VƏZİYYƏTİ YÜKLƏNƏNDƏN SONRA VERİLİR.
      NƏ TAPILDI: `evaluateAge` dərhal çağırılırdı; hesabın yaşı (`/api/me/age-state`) hələ
      gəlməyibsə `accountAge === null` → «birthDate». Cihaz modalı isə `loadAgeIfNeeded`-i
      gözləyir (`ui/detailModal.ts` → `reviewAge`).
      ƏVVƏL: doğum tarixi OLAN hesab 18+ kartda («Pərdə») seçici görürdü, təsdiq 409 alırdı,
      «Tarix qeyd olunmadı» yazılırdı və film heç vaxt açılmırdı.
      NİYƏ BELƏ: «allowed» yaşdan asılı deyil (giriş yoxdur və ya hədd 18-dən aşağıdır),
      ona görə dərhal açılır; qalan hökmlər yaş gələndən sonra yenidən verilir.
      `loadAgeIfNeeded` əvvəlki uğursuz cəhddən sonra sorğunu TƏKRARLAYIR (bayraq uğurda
      qalxır), paralel çağırışları isə bir sorğuya yığır.
  */
  let hint: DialogHandle | null = null
  const timer = window.setTimeout(() => {
    if (ticket !== gateTicket) return
    hint = noticeDialog(T('Yaş məhdudiyyəti'), T('Yoxlanılır…'), () => {
      gateTicket += 1
    })
  }, AGE_WAIT_HINT_MS)
  void loadAgeIfNeeded().then(() => {
    window.clearTimeout(timer)
    const shown = hint as DialogHandle | null
    if (shown) shown.close()
    if (ticket !== gateTicket) return
    decideAge(card, open)
  })
}

function decideAge(card: ContentCard, open: () => void): void {
  const verdict = evaluateAge(card.ageLimit)
  if (verdict === 'allowed') {
    open()
    return
  }
  if (verdict === 'tooYoung') {
    showTooYoung(card)
    return
  }
  askBirthDate(card, open)
}

function showTooYoung(card: ContentCard): void {
  showModal(
    T('Yaş məhdudiyyəti'),
    T('Bu material ') + (card.ageLimit || AGE_GATE_FROM) + T('+ üçündür. Hesabındakı yaş uyğun deyil.'),
  )
}

/** Sadə bildiriş modalı. */
export function showModal(title: string, text: string): void {
  noticeDialog(title, text)
}

function noticeDialog(title: string, text: string, onCancel?: () => void): DialogHandle {
  const overlay = el('div', 'web-modal')
  const card = el('div', 'web-modal__card')
  card.appendChild(el('h2', 'web-modal__title', title))
  card.appendChild(el('p', 'web-modal__text', text))
  let handle: DialogHandle | null = null
  const close = webButton(T('Bağla'), () => {
    if (handle) handle.cancel()
  })
  card.appendChild(close)
  overlay.appendChild(card)
  handle = openDialog(overlay, onCancel)
  close.focus()
  return handle
}

/**
 * Doğum tarixi soruşan modal.
 *
 * ⚠ ÜÇ `<select>`, `<input type="date">` DEYİL: doğma tarix seçicisi TV
 *   brauzerində ya ümumiyyətlə açılmır, ya da pultla idarə olunmayan üzən
 *   təqvim verir. Üç siyahı isə Tab + ox ilə hər yerdə işləyir.
 * ⚠ SERVER TARİXİ BİR DƏFƏ QƏBUL EDİR (`saveBirthDate` → 409) — 409-da yaş serverdən
 *   oxunur (`lib/ageGate.ts`). Uğursuzluqda modal bağlanmır və səbəb yazılır.
 * ⚠ UĞURDAN SONRA HÖKM YENİDƏN VERİLİR (2026-10-23): əvvəl `onAllowed` şərtsiz
 *   çağırılırdı — 409-dan oxunan və ya yeni yazılan tarix yaşı çatmayan çıxsa da
 *   modal açılardı. İndi yaş çatmırsa «yaş uyğun deyil» bildirişi çıxır.
 */
function askBirthDate(target: ContentCard, onAllowed: () => void): void {
  const overlay = el('div', 'web-modal')
  const card = el('div', 'web-modal__card')
  card.appendChild(el('h2', 'web-modal__title', T('Doğum tarixin')))
  card.appendChild(el('p', 'web-modal__text', T('Bu materialı açmaq üçün yaşını təsdiqlə.')))

  const row = el('div')
  row.style.marginBottom = '18px'
  const day = selectBox(1, 31, T('Gün'))
  const month = selectBox(1, 12, T('Ay'))
  const nowYear = new Date().getFullYear()
  const year = selectBox(nowYear - 100, nowYear, T('İl'), true)
  row.appendChild(day)
  row.appendChild(month)
  row.appendChild(year)
  card.appendChild(row)

  const note = el('p', 'web-qr__note', '')
  card.appendChild(note)

  let handle: DialogHandle | null = null
  let saving = false
  /** «Bağla» · «Geri» · naviqasiya ilə bağlanıbsa gec gələn cavab tətbiq olunmur. */
  let cancelled = false
  /*
    ⚠ DÜYMƏ SÖNDÜRÜLMÜR, BAYRAQ İŞLƏDİLİR (2026-10-23): fokuslanmış düymə `disabled`
      olanda brauzer fokusu `<body>`-yə atır — pultla seçən istifadəçi cavabdan sonra
      fokusu itirmiş görürdü.
  */
  const submit = webButton(T('Təsdiqlə'), () => {
    if (saving) return
    if (!day.value || !month.value || !year.value) {
      setText(note, T('Gün, ay və ili seç.'))
      return
    }
    const iso = year.value + '-' + pad2(month.value) + '-' + pad2(day.value)
    saving = true
    setText(note, T('Yoxlanılır…'))
    void saveBirthDate(iso).then((ok) => {
      saving = false
      if (cancelled) return
      const verdict = ok ? evaluateAge(target.ageLimit) : 'birthDate'
      if (verdict === 'birthDate') {
        setText(note, T('Doğum tarixi yadda saxlanmadı. Yenidən yoxlayın.'))
        return
      }
      if (handle) handle.close()
      if (verdict === 'allowed') onAllowed()
      else showTooYoung(target)
    })
  })
  card.appendChild(submit)
  card.appendChild(
    webButton(
      T('Bağla'),
      () => {
        if (handle) handle.cancel()
      },
      true,
    ),
  )

  overlay.appendChild(card)
  handle = openDialog(overlay, () => {
    cancelled = true
  })
  day.focus()
}

function selectBox(from: number, to: number, label: string, descending?: boolean): HTMLSelectElement {
  const node = document.createElement('select')
  node.className = 'web-input web-focusable'
  node.style.width = '30%'
  node.style.display = 'inline-block'
  node.style.marginRight = '3%'

  const empty = document.createElement('option')
  empty.value = ''
  empty.appendChild(document.createTextNode(label))
  node.appendChild(empty)

  for (let i = 0; i <= to - from; i++) {
    const value = descending ? to - i : from + i
    const option = document.createElement('option')
    option.value = String(value)
    option.appendChild(document.createTextNode(String(value)))
    node.appendChild(option)
  }
  return node
}

/** `padStart` Chrome 57+-dır — `lib/compat.ts`-dəki eyni səbəb. */
function pad2(value: string): string {
  return value.length >= 2 ? value : '0' + value
}
